import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
    Form,
    FormFields,
    FormActions,
    useNotifications,
    FormTextField,
    FormNumberTextField,
    mapEmptyStringToUndefined,
    mapNullableToUndefined
} from "sprint-common-web";

import { validateDuration } from "../../../../../../content-ingestion/file-records/common/utils/validateDuration";
import { useOnPremAssetDetails } from "../../../common/hooks/useOnPremAssetDetails";
import { ensureOnPremAudioDetails } from "../../../OnPremAssetDetails";
import { useEditOnPremAudioAssetTechnical } from "./useEditOnPremAudioAssetTechnical";

export interface AudioTechnicalFormProps {
    onDismiss(): void;
    onSubmit(newAssetId: string): void;
    assetId: string;
}

interface AudioTechnicalFormData {
    audioFormat: string;
    duration: string;
    sampleRate: number;
    channelsCount: number;
    audioBitRate: number;
}

export const OnPremAudioAssetTechnicalForm = (props: AudioTechnicalFormProps): JSX.Element => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const { success, error } = useNotifications();
    const { data: assetDetails } = useOnPremAssetDetails();
    const { updateOnPremAudioAssetTechnical } = useEditOnPremAudioAssetTechnical({
        invalidationKey: assetDetails?.assetId
    });

    if (assetDetails) {
        ensureOnPremAudioDetails(assetDetails);
    }

    const { handleSubmit, errors, control, formState, reset } = useForm<AudioTechnicalFormData>();

    useEffect(() => {
        if (assetDetails?.technical) {
            reset({
                audioFormat: assetDetails.technical.audioFormat,
                duration: assetDetails.technical.duration,
                sampleRate: assetDetails.technical.sampleRate,
                channelsCount: assetDetails.technical.channelsCount,
                audioBitRate: assetDetails.technical.audioBitRate
            });
        }
    }, [assetDetails]);

    const handleUpdate = async ({
        audioFormat,
        duration,
        sampleRate,
        channelsCount,
        audioBitRate
    }: AudioTechnicalFormData) => {
        setIsLoading(true);

        try {
            const updateResponse = await updateOnPremAudioAssetTechnical.mutateAsync({
                assetId: props.assetId,
                audioFormat: mapEmptyStringToUndefined(audioFormat),
                duration: mapEmptyStringToUndefined(duration),
                sampleRate: mapNullableToUndefined(sampleRate),
                channelsCount: mapNullableToUndefined(channelsCount),
                audioBitRate: mapNullableToUndefined(audioBitRate)
            });

            props.onSubmit(updateResponse.newAssetId);
            success("Audio asset technical details updated successfully.");
        } catch (e) {
            error("Failed to update Audio asset technical details.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Form onSubmit={handleSubmit(handleUpdate)}>
            <FormFields>
                <FormTextField label="Audio format" name="audioFormat" control={control} errors={errors} />
                <FormTextField
                    rules={{ validate: validateDuration }}
                    label="Duration"
                    name="duration"
                    control={control}
                    errors={errors}
                    mask="99:99:99"
                />
                <FormNumberTextField label="Sample rate" name="sampleRate" control={control} errors={errors} />
                <FormNumberTextField label="Audio bitrate" name="audioBitRate" control={control} errors={errors} />
                <FormNumberTextField label="Channels count" name="channelsCount" control={control} errors={errors} />
                <FormActions
                    onCancel={props.onDismiss}
                    isLoading={formState.isValidating || formState.isSubmitting || isLoading}
                    submitDisabled={!formState.isDirty}
                />
            </FormFields>
        </Form>
    );
};
